import React, { Fragment } from 'react';

import { track } from '@src/services/analytics';
import { useWidgetId } from '@src/core/IdProvider';
import Calendar from '@src/components/reusable/Calendar';
import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { MonthTuple, IntervalDate } from '@src/common/calendar';
import {
  loadAvailableDates,
  goToNextStep,
  productSelector,
  productScheduleSelector,
  selectAvailableDate,
  tariffsState,
  adderResumeSelector,
} from '@src/features/product';
import { appInfoSelector } from '@src/features/auth';
import usePackageIdParam from '@src/hooks/usePackageIdParam';
import ProductPackageCalendarPreviousStepButton from '../ProductPackage/ProductPackageCalendarPreviousStepButton';
import useBreakpoint from '@src/hooks/useBreakpoint';

const ProductAdderCalendar = () => {
  const widgetId = useWidgetId();
  const { productId } = useAppSelector(adderResumeSelector(widgetId));

  const product = useAppSelector(productSelector(productId));
  const { selectedDate } = useAppSelector(tariffsState(widgetId));
  const { dates, pending } = useAppSelector(productScheduleSelector(productId));
  const dispatch = useAppDispatch();
  const appInfo = useAppSelector(appInfoSelector);

  const packageId = usePackageIdParam();
  const { isDesktop } = useBreakpoint();

  const onMonthChange = (_: MonthTuple, interval: IntervalDate[]) => {
    const dateFrom = interval[0]?.dateString as string;
    const dateTo = interval[interval.length - 1]?.dateString as string;

    dispatch(loadAvailableDates(productId, dateFrom, dateTo));
  };

  const handleDateSelect = (date: string) => {
    track('productAdderDateSelected', {
      productId: product?.id ?? '',
      productName: product?.name ?? '',
      date,
    });

    dispatch(selectAvailableDate({ id: widgetId, value: date }));
    dispatch(goToNextStep(widgetId));
  };

  return (
    <Fragment>
      <Calendar
        dates={dates}
        pending={pending}
        defaultDateReference={selectedDate}
        onMonthChange={onMonthChange}
        onDateSelect={handleDateSelect}
        enableAutoLoad={true}
        tariffToShowThePrice={appInfo?.featuredTariffPreviewMode}
      />

      {packageId && !isDesktop ? <ProductPackageCalendarPreviousStepButton /> : null}
    </Fragment>
  );
};

export default ProductAdderCalendar;
